import {
  type FeatureFlagValues,
  LOCAL_FEATURE_FLAG_VALUES,
} from '@his/feature-flags';
import { useSuspenseQuery } from '@tanstack/react-query';
import {
  type PropsWithChildren,
  type ReactNode,
  useLayoutEffect,
  useState,
} from 'react';
import { FEATURE_FLAGS_CONFIG } from '@/lib/env';
import { getFeatureFlagsQueryOptions } from '@/services/use-query-feature-flags';
import { FeatureFlagsContext } from './use-feature-flags';

type FeatureFlagProviderProps = PropsWithChildren;

type FeatureFlagsValueProps = {
  flags: FeatureFlagValues;
  children: ReactNode;
};

const FeatureFlagsValue = ({ flags, children }: FeatureFlagsValueProps) => (
  <FeatureFlagsContext.Provider value={{ flags }}>
    {children}
  </FeatureFlagsContext.Provider>
);

const RemoteFeatureFlagsLoader = ({
  onLoaded,
}: {
  onLoaded: (flags: FeatureFlagValues) => void;
}) => {
  const { data, error } = useSuspenseQuery(getFeatureFlagsQueryOptions());

  useLayoutEffect(() => onLoaded(data), [data, onLoaded]);
  if (error) {
    throw error;
  }

  return null;
};

const RemoteFeatureFlagProvider = ({ children }: FeatureFlagProviderProps) => {
  const [flags, setFlags] = useState<FeatureFlagValues>();

  if (flags === undefined) {
    return <RemoteFeatureFlagsLoader onLoaded={setFlags} />;
  }

  return <FeatureFlagsValue flags={flags}>{children}</FeatureFlagsValue>;
};

export const FeatureFlagProvider = ({ children }: FeatureFlagProviderProps) => {
  if (FEATURE_FLAGS_CONFIG.mode === 'local') {
    return (
      <FeatureFlagsValue flags={LOCAL_FEATURE_FLAG_VALUES}>
        {children}
      </FeatureFlagsValue>
    );
  }

  return <RemoteFeatureFlagProvider>{children}</RemoteFeatureFlagProvider>;
};
